import { useQuery } from '@tanstack/react-query';
import capitalize from 'lodash/capitalize';

import { useEcomStoreSelector, useMenuService } from '@/data';
import {
  EnumCustomerType,
  GProduct,
  SortBySelectOption,
  TreezStore,
} from '@/types';
import { buildSearchBodyFromParams, useSearchService } from '@/opensearch';
import { PRODUCT_FILTER_ATTRIBUTES } from '@/constants';
import { getProductsFromOpenSearchResponse } from '@/utils';
import { useIsServerSide } from './useIsServerSide';

interface UseGetProductByCategoryProps {
  store?: TreezStore;
  customerType?: EnumCustomerType;

  products?: GProduct[];

  // limit and sort
  sortBy?: SortBySelectOption;
  limit: number;

  // filters
  filterByStore?: boolean;
  filterByCategory?: string;
  filterByBrand?: string;
  filterByGroupId?: string;
  filterByOnSale?: boolean;
  locationSlug?: string;
}

export const useProductsByCategory = ({
  store,
  customerType,
  limit,
  sortBy,
  products: initialProducts,
  filterByStore,
  filterByCategory,
  filterByBrand,
  filterByGroupId,
  filterByOnSale,
  locationSlug,
}: UseGetProductByCategoryProps) => {
  const { customer_type, provider_inventory_location_id } =
    useEcomStoreSelector(['customer_type', 'provider_inventory_location_id']);
  const { isServerSide } = useIsServerSide();
  const customerTypeToUse = isServerSide ? customerType : customer_type;

  const dispensaryName = store?.shortName || '';

  const queryKey = [
    'productsByCategory',
    dispensaryName,
    customerTypeToUse,
    filterByCategory,
    filterByGroupId,
    filterByOnSale,
    filterByBrand,
    filterByStore,
    provider_inventory_location_id,
  ];

  const { searchProductsByGroupId } = useMenuService();
  const { search } = useSearchService({
    selectedStore: store,
    selectedCustomerType: customerTypeToUse,
  });

  // in case that locationSlug is present, check if there is a selected store (dispensaryName)
  const isEnabled = filterByStore && (locationSlug ? !!dispensaryName : true);

  const { data, isLoading, error, isError } = useQuery({
    queryKey,
    queryFn: async () => {
      const sortParam = {
        sort:
          sortBy && sortBy.value !== 'default'
            ? `${sortBy.value.field}${capitalize(sortBy.value.order)}`
            : '',
      };

      const idParam: { id: string[] } = { id: [] };

      const categoryParam = {
        [PRODUCT_FILTER_ATTRIBUTES._CATEGORY]:
          filterByCategory && filterByCategory !== 'default'
            ? [filterByCategory.toUpperCase()]
            : [],
      };

      const brandParam = {
        [PRODUCT_FILTER_ATTRIBUTES._BRAND]:
          filterByBrand && filterByBrand !== 'default'
            ? [filterByBrand.toUpperCase()]
            : [],
      };

      const onSaleParam = {
        [PRODUCT_FILTER_ATTRIBUTES._ON_SALE]: filterByOnSale ? ['true'] : [],
      };

      if (filterByGroupId) {
        const productGroup = await searchProductsByGroupId({
          id: filterByGroupId,
        });

        idParam.id.push(...productGroup.map(e => e.id));
      }

      const { searchBody } = buildSearchBodyFromParams({
        searchParams: {
          ...sortParam,
          ...idParam,
          ...categoryParam,
          ...brandParam,
          ...onSaleParam,
        },
        filters: {
          isAboveThreshold: [true],
          isHideFromMenu: [false],
        },
        limit: limit ? parseInt(limit?.toString(), 10) : 20,
      });

      const result = await search({ searchBody });

      const products = getProductsFromOpenSearchResponse(result);

      return products;
    },

    enabled: isEnabled,
    initialData: initialProducts,
  });

  return {
    error,
    isError,
    isLoading,
    products: data ?? [],
    total: 0,
  };
};
